import React from 'react';

import Application from 'types/application';
import { PricingPlan } from 'types/pricing_plan';

import { useAPI } from 'hooks/api/useAPI';
import { useAccount } from 'hooks/api/useAccount';
import { useApplications } from 'hooks/api/useApplications';
import { useAuthenticatedQueries } from 'hooks/api/useAuthenticatedQuery';

type OrganizationRingPlan = { organizationId: string; isRing: boolean };

export function useOwnedRingApplications() {
  const { client } = useAPI();
  const {
    data: applications,
    isLoading: applicationsLoading,
    isError: applicationsError,
    refetch: refetchApplications,
  } = useApplications();
  const {
    data: account,
    isLoading: accountLoading,
    isError: accountError,
    refetch: refetchAccount,
  } = useAccount();

  const ownedApplications = React.useMemo(() => {
    if (!applications || !account) {
      return [] as Application[];
    }

    return applications.filter((application) =>
      account.roles.some(
        (role) =>
          role.name === 'owner' &&
          role.organization_id === application.organization_id
      )
    );
  }, [applications, account]);

  const ownedOrganizationIds = React.useMemo(
    () => [
      ...new Set(
        ownedApplications.map(({ organization_id }) => organization_id)
      ),
    ],
    [ownedApplications]
  );

  const planQueries = useAuthenticatedQueries(
    ownedOrganizationIds.map((organizationId) => ({
      queryKey: ['organization-ring-plan', organizationId],
      queryFn: async (): Promise<OrganizationRingPlan> => {
        const { data } = await client.get<PricingPlan | null>(
          `/internal/orgs/${organizationId}/plan`
        );
        return {
          organizationId,
          isRing: Boolean(data?.name?.includes('Ring')),
        };
      },
      staleTime: Infinity,
    }))
  );

  const plansLoading = planQueries.some((query) => query.isInitialLoading);
  const plansError = planQueries.some((query) => query.isError);

  const ringOrganizationIds = React.useMemo(
    () =>
      new Set(
        planQueries
          .map((query) => query.data as OrganizationRingPlan | undefined)
          .filter((data): data is OrganizationRingPlan => Boolean(data?.isRing))
          .map(({ organizationId }) => organizationId)
      ),
    [planQueries]
  );

  const ownedRingApplications = React.useMemo(
    () =>
      ownedApplications.filter(({ organization_id }) =>
        ringOrganizationIds.has(organization_id)
      ),
    [ownedApplications, ringOrganizationIds]
  );

  const refetch = () => {
    void refetchApplications();
    void refetchAccount();
    planQueries.forEach((query) => {
      void query.refetch();
    });
  };

  return {
    ownedRingApplications,
    isLoading: applicationsLoading || accountLoading || plansLoading,
    isError: applicationsError || accountError || plansError,
    refetch,
  };
}
